import React from "react";
import {Link} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";
import {ChevronDown, LogOut, Send, Settings, KeyRound} from "@/components/icons";
import {requestAccountReset, fetchAccountResetAvailability, type ResetChannel} from "@/lib/api";
import {useResetPassword} from "@/components/ResetPasswordAction";
import {ChangePasswordDialog} from "@/components/ChangePasswordDialog";
import {useAuth} from "@/app/AuthContext";
import {toast} from "sonner";
import {UserAvatar} from "@/components/icons";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
} from "@/components/ui/dropdown-menu";


export default function UserMenu({user}: { user: { id: number, username: string; role: string } | null }) {
    // Account Settings hosts the transactional-SMS opt-in AND the Connect-Google
    // control, so it is shown when either of those features is enabled.
    const {smsPasswordResetEnabled, googleLoginEnabled} = useAuth();
    const showAccountSettings = smsPasswordResetEnabled || googleLoginEnabled;
    // Availability + masked destinations come from the backend (authoritative);
    // the reset itself is always bound server-side to the current session user.
    const availabilityQ = useQuery({
        queryKey: ["reset-availability", "account"],
        queryFn: fetchAccountResetAvailability,
        enabled: Boolean(user),
    });
    const avail = availabilityQ.data;

    async function initiate(channel: ResetChannel) {
        try {
            await requestAccountReset(channel);
            toast.success(channel === "sms"
                ? "Check your phone for a password reset code"
                : "Check your email for a password reset link");
        } catch (e: any) {
            // Prefer the backend's friendly message (cooldown / rate-limited /
            // send-failed / not-configured); fall back to the error code.
            toast.error(e?.body?.message || e?.message || "Failed to request password reset");
        }
    }

    const reset = useResetPassword({
        emailAvailable: avail?.email_reset_available ?? false,
        smsAvailable: avail?.sms_reset_available ?? false,
        maskedEmail: avail?.masked_email,
        maskedPhone: avail?.masked_phone,
        loading: availabilityQ.isLoading,
        error: availabilityQ.isError,
        initiate,
    });

    // Self-service "Change password" — set a new password directly while logged
    // in (no recovery link/code). Rendered outside the dropdown so it survives
    // the menu closing, same pattern as the reset dialog.
    const [changeOpen, setChangeOpen] = React.useState(false);

    return (
        <>
            {!user && null}
            {user &&
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            className="
                                inline-flex items-center gap-3
                                h-auto min-h-0 px-2 py-1.5
                                rounded-2xl
                                hover:bg-muted
                                transition-colors outline-none
                            "
                        >
                            <UserAvatar className="w-10 rounded-full" />

                            <div className="hidden sm:flex flex-col items-start leading-tight">
                                <span className="text-sm font-medium text-foreground">
                                  {user.username}
                                </span>
                                <span className="text-xs text-foreground/60">
                                  {user.role}
                                </span>
                            </div>

                            <ChevronDown className="h-4 w-4 text-foreground/50"/>
                        </button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent align="end" className="w-64">
                        {showAccountSettings &&
                            <DropdownMenuItem asChild>
                                <Link to="/account">
                                    <Settings className="h-4 w-4"/>
                                    Account settings
                                </Link>
                            </DropdownMenuItem>}
                        <DropdownMenuItem onSelect={() => setChangeOpen(true)}>
                            <KeyRound className="h-4 w-4"/>
                            Change password
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            disabled={reset.disabled}
                            title={reset.title}
                            onSelect={() => reset.onTrigger()}
                        >
                            <Send className="h-4 w-4"/>
                            {reset.label}
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                            <a href="/logout">
                                <LogOut className="h-4 w-4"/>
                                Logout
                            </a>
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>}

            {/* Rendered OUTSIDE the dropdown so it survives the menu closing. */}
            {reset.dialog}
            {user &&
                <ChangePasswordDialog
                    open={changeOpen}
                    onClose={() => setChangeOpen(false)}
                    userId={user.id}
                    role={user.role}
                />}
        </>
    )
}
